// İSG Portal — Alert (form/sayfa düzeyi bildirim). Token'lı; `role` erişilebilir.
//
// `carbon:` (S33 planı Stage 2 "Alert → InlineNotification", Slice 1b) — sol 3px
// renkli çizgi + ikon (durumu RENK TEK BAŞINA taşımasın, bkz. depo a11y kuralı),
// kutu dolgusu KALKAR (`rounded-md` → `rounded-none`). İkon her zaman DOM'dadır,
// yalnız `hidden carbon:flex` ile taşınır — Alert'in JSX'i HER derlemede aynı
// bayt, görünürlük yalnız Carbon derlemesinde değişir (mekanizma §2.1, Card/
// Button'la aynı desen). Glif tam olarak Carbon'un dolu-daire "!" rozetini
// TAŞIMAZ — repo'nun ikon seti `lucide-react`'tir (yeni bir SVG asseti eklenmez,
// plan §2.7'nin "literal değeri token'a çevir" ilkesinin ikon karşılığı).
//
// `onDismiss` (yeni, opsiyonel) — README'nin "32px ghost ×" alanı. Verilmezse
// (bugünkü TÜM çağıranlar) hiçbir şey render edilmez, davranış AYNI kalır.

import { CheckCircle2, Info, TriangleAlert, X, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from './cn';

type Tone = 'danger' | 'success' | 'info' | 'warning';

const tones: Record<Tone, string> = {
  danger:
    'bg-danger-subtle text-danger border-danger/30 carbon:border-l-danger carbon:text-fg carbon:bg-danger-subtle',
  success:
    'bg-success-subtle text-success border-success/30 carbon:border-l-success carbon:text-fg carbon:bg-success-subtle',
  info: 'bg-surface-muted text-fg border-border carbon:border-l-info carbon:bg-info-subtle',
  warning:
    'bg-warning text-warning-foreground border-warning-foreground/25 carbon:border-l-warning carbon:bg-warning-subtle carbon:text-fg',
};

const icons: Record<Tone, typeof Info> = {
  danger: XCircle,
  success: CheckCircle2,
  info: Info,
  warning: TriangleAlert,
};

const iconTone: Record<Tone, string> = {
  danger: 'text-danger',
  success: 'text-success',
  info: 'text-info',
  warning: 'text-warning',
};

export function Alert({
  tone = 'info',
  children,
  className,
  onDismiss,
  dismissLabel = 'Kapat',
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
  /** Verilirse 32px ghost `×` gösterilir (Carbon "optional 32px ghost ×"). */
  onDismiss?: () => void;
  /** Kapat düğmesinin erişilebilir etiketi (i18n ile). */
  dismissLabel?: string;
}) {
  const Icon = icons[tone];
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'rounded-md border px-3 py-2 text-sm',
        'carbon:flex carbon:items-start carbon:gap-2 carbon:rounded-none carbon:border-0 carbon:border-l-[3px] carbon:p-3 carbon:pl-4',
        tones[tone],
        className,
      )}
    >
      <Icon
        aria-hidden="true"
        className={cn('hidden h-5 w-5 shrink-0 carbon:mt-px carbon:flex', iconTone[tone])}
      />
      <div className="carbon:min-w-0 carbon:flex-1">{children}</div>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="hidden shrink-0 items-center justify-center rounded p-1 text-muted hover:bg-surface-hover hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring carbon:flex carbon:h-8 carbon:w-8"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}
